import { Helmet } from 'react-helmet';

interface MetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  type?: string;
}

export const MetaTags = ({
  title = 'Abrovinc - Expert IT Consultancy in Scalability, Cloud & Microservices',
  description = 'Abrovinc provides expert IT consultancy services specializing in scalable architectures, cloud solutions, microservices, and modern software development. Founded by Steve Widinghoff in 2022, based in Umeå, Sweden, we partner with leading companies like Spotify and Volvo.',
  keywords = 'IT consulting, scalability, cloud computing, microservices, backend development, architecture, GCP, AWS, Java, Kotlin, React, TypeScript, DevOps, Sweden, Umeå',
  url = 'https://abrovinc.com',
  type = 'website'
}: MetaTagsProps) => {
  const ogImage = 'https://abrovinc.com/favicon.svg';
  const siteName = 'Abrovinc';

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Abrovinc" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="Abrovinc - IT Consultancy Company" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Abrovinc - IT Consultancy Company" />

      {/* Structured Data for Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Abrovinc',
          url: 'https://abrovinc.com',
          logo: ogImage,
          description: description,
          foundingDate: '2022',
          founder: {
            '@type': 'Person',
            name: 'Steve Widinghoff',
            jobTitle: 'Founder & Senior Consultant',
            sameAs: 'https://www.linkedin.com/in/steve-widinghoff'
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Umeå',
            addressCountry: 'SE'
          },
          sameAs: ['https://www.linkedin.com/company/abrovinc'],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'Customer Service',
            availableLanguage: ['English', 'Swedish']
          }
        })}
      </script>

      {/* Structured Data for Website */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteName,
          url: 'https://abrovinc.com',
          description: description,
          publisher: {
            '@type': 'Organization',
            name: 'Abrovinc'
          }
        })}
      </script>
    </Helmet>
  );
};
